/**
 * <motorcycle-selector>
 *
 * Vanilla web component for the Forty5° storefront. Shows a "Select your
 * motorcycle" affordance in the header that, when clicked, opens cascading
 * Year → Make → Model → Engine dropdowns sourced from /assets/bikes.json.
 *
 * Once a customer picks a bike, the selection is:
 *   - persisted to localStorage    (key: `forty5_selected_bike`)
 *   - reflected in the URL         (param: `bike=<id>`)
 *   - announced via a custom event (`forty5:bike-selected`) so downstream
 *     components (collection-page filter, product-card fitment badge) can
 *     react without coupling to this component.
 *
 * The bikes.json file is cached in localStorage after first fetch so
 * subsequent page loads don't re-download (browser cache-control would
 * also work but the explicit cache lets us version-bump via a single key).
 *
 * Design notes:
 *   - Zero framework, zero external deps. ~3 KB minified target.
 *   - All styles in motorcycle-selector.css using Dawn's CSS custom props.
 *   - Closed-shadow-DOM is intentionally NOT used so Dawn's font/color
 *     tokens cascade in.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'forty5_selected_bike';
  const BIKES_CACHE_KEY = 'forty5_bikes_cache_v2'; // v2: generation split (2026-10-10)
  const URL_PARAM = 'bike';
  const EVENT_NAME = 'forty5:bike-selected';
  // The rider's own model year and a "2022 Road Glide ST" label. Bike entries
  // span ranges ("2022-2026"); showing the range back made riders think they
  // were being shown newer-generation parts.
  const YEAR_KEY = 'forty5_selected_bike_year';
  const LABEL_KEY = 'forty5_selected_bike_label';
  // Search & Discovery filter on custom.compatible_bikes — server-side "fits
  // my bike" across the whole catalogue. Shopify ignores the param while the
  // filter is not enabled, so it is always safe to send.
  const FILTER_PARAM = 'filter.p.m.custom.compatible_bikes';
  // 2026-10-10: entries that crossed a generation change were split. A rider's
  // saved bike moves to the new entry covering their year (or the first one
  // when no year was saved). [year_min, year_max, new id]
  const SPLIT_IDS = {
    'harley-davidson-road-glide-2017-2026-m8': [[2017, 2023, 'harley-davidson-road-glide-2017-2023-m8'], [2024, 2026, 'harley-davidson-road-glide-2024-2026-m8-gen2']],
    'harley-davidson-road-glide-st-2022-2026-m8': [[2022, 2023, 'harley-davidson-road-glide-st-2022-2023-m8'], [2025, 2026, 'harley-davidson-road-glide-st-2025-2026-m8-gen2']],
    'harley-davidson-road-glide-special-2017-2026-m8': [[2017, 2023, 'harley-davidson-road-glide-special-2017-2023-m8']],
    'harley-davidson-road-glide-limited-2020-2026-m8': [[2020, 2023, 'harley-davidson-road-glide-limited-2020-2023-m8'], [2024, 2026, 'harley-davidson-road-glide-limited-2024-2026-m8-gen2']],
    'harley-davidson-road-glide-ultra-2011-2019': [[2011, 2016, 'harley-davidson-road-glide-ultra-2011-2016-twin-cam'], [2017, 2019, 'harley-davidson-road-glide-ultra-2017-2019-m8']],
    'harley-davidson-street-glide-2017-2026-m8': [[2017, 2023, 'harley-davidson-street-glide-2017-2023-m8'], [2024, 2026, 'harley-davidson-street-glide-2024-2026-m8-gen2']],
    'harley-davidson-street-glide-special-2014-2026': [[2014, 2016, 'harley-davidson-street-glide-special-2014-2016-twin-cam'], [2017, 2023, 'harley-davidson-street-glide-special-2017-2023-m8']],
    'harley-davidson-street-glide-st-2022-2026-m8': [[2022, 2023, 'harley-davidson-street-glide-st-2022-2023-m8'], [2025, 2026, 'harley-davidson-street-glide-st-2025-2026-m8-gen2']],
    'harley-davidson-electra-glide-2017-2026-m8': [[2017, 2023, 'harley-davidson-electra-glide-2017-2023-m8'], [2024, 2026, 'harley-davidson-electra-glide-2024-2026-m8-gen2']],
    'harley-davidson-electra-glide-ultra-limited-2010-2026': [[2010, 2016, 'harley-davidson-electra-glide-ultra-limited-2010-2016-twin-cam'], [2017, 2023, 'harley-davidson-electra-glide-ultra-limited-2017-2023-m8'], [2024, 2026, 'harley-davidson-electra-glide-ultra-limited-2024-2026-m8-gen2']],
    'harley-davidson-electra-glide-ultra-classic-1999-2019': [[1999, 2016, 'harley-davidson-electra-glide-ultra-classic-1999-2016-twin-cam'], [2017, 2019, 'harley-davidson-electra-glide-ultra-classic-2017-2019-m8']],
    'harley-davidson-road-king-classic-1998-2019': [[1998, 2016, 'harley-davidson-road-king-classic-1998-2016-evolution-twin-cam'], [2017, 2019, 'harley-davidson-road-king-classic-2017-2019-m8']],
    'harley-davidson-freewheeler-2015-2026': [[2015, 2016, 'harley-davidson-freewheeler-2015-2016-twin-cam'], [2017, 2026, 'harley-davidson-freewheeler-2017-2026-m8']],
    'harley-davidson-cvo-road-glide-2014-2026': [[2014, 2016, 'harley-davidson-cvo-road-glide-2014-2016-cvo-twin-cam'], [2017, 2022, 'harley-davidson-cvo-road-glide-2017-2022-cvo-m8'], [2023, 2026, 'harley-davidson-cvo-road-glide-2023-2026-cvo-m8-gen2']],
    'harley-davidson-cvo-street-glide-2010-2026': [[2010, 2016, 'harley-davidson-cvo-street-glide-2010-2016-cvo-twin-cam'], [2017, 2022, 'harley-davidson-cvo-street-glide-2017-2022-cvo-m8'], [2023, 2026, 'harley-davidson-cvo-street-glide-2023-2026-cvo-m8-gen2']],
    'harley-davidson-cvo-limited-2014-2021': [[2014, 2016, 'harley-davidson-cvo-limited-2014-2016-cvo-twin-cam'], [2017, 2021, 'harley-davidson-cvo-limited-2017-2021-cvo-m8']]
  };

  function migrateId(id, year) {
    const parts = SPLIT_IDS[id];
    if (!parts) return id;
    const hit = year && parts.find(([min, max]) => year >= min && year <= max);
    return (hit || parts[0])[2];
  }
  // Where "Show parts for my bike" lands when confirmed on a page with no
  // product grid (home, PDP, content pages). Override per mount with
  // data-results-url.
  const DEFAULT_RESULTS_URL = '/collections'; /* category hub — /collections/all is too big to filter */
  // Any link ending in this hash opens the selector (e.g. the homepage hero's
  // "Find parts for my bike" → /collections/all#find-my-bike).
  const OPEN_HASH = '#find-my-bike';

  /* ---------------------------------------------------------------- */
  /* Bike data — fetch + cache                                         */
  /* ---------------------------------------------------------------- */

  let bikesPromise = null;

  function loadBikes(jsonUrl) {
    if (bikesPromise) return bikesPromise;

    // Try sessionStorage cache first (cheap)
    try {
      const cached = sessionStorage.getItem(BIKES_CACHE_KEY);
      if (cached) {
        bikesPromise = Promise.resolve(JSON.parse(cached));
        return bikesPromise;
      }
    } catch (_e) {
      /* sessionStorage may be unavailable in some contexts */
    }

    bikesPromise = fetch(jsonUrl, { credentials: 'omit' })
      .then((r) => {
        if (!r.ok) throw new Error(`bikes.json fetch failed: ${r.status}`);
        return r.json();
      })
      .catch((err) => {
        bikesPromise = null;
        throw err;
      })
      .then((data) => {
        try {
          sessionStorage.setItem(BIKES_CACHE_KEY, JSON.stringify(data));
        } catch (_e) {
          /* over quota or unavailable — ignore, just lose the cache */
        }
        return data;
      });

    return bikesPromise;
  }

  /* ---------------------------------------------------------------- */
  /* Selection state                                                   */
  /* ---------------------------------------------------------------- */

  function readUrlParam() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(URL_PARAM);
    } catch (_e) {
      return null;
    }
  }

  function readStorage() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      return null;
    }
  }

  function writeStorage(id, year, label) {
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
        localStorage.setItem(YEAR_KEY, String(year));
        localStorage.setItem(LABEL_KEY, label);
      } else {
        [STORAGE_KEY, YEAR_KEY, LABEL_KEY].forEach((k) => localStorage.removeItem(k));
      }
    } catch (_e) {
      /* private mode or storage disabled — selection is session-only */
    }
  }

  function readStoredYear() {
    try {
      return Number(localStorage.getItem(YEAR_KEY)) || null;
    } catch (_e) {
      return null;
    }
  }

  function bikeLabel(bike, year) {
    return year ? `${year} ${bike.model}` : bike.display_name;
  }

  function writeUrlParam(id) {
    try {
      const url = new URL(window.location.href);
      if (id) url.searchParams.set(URL_PARAM, id);
      else url.searchParams.delete(URL_PARAM);
      window.history.replaceState({}, '', url.toString());
    } catch (_e) {
      /* ignore */
    }
  }

  function dispatchSelection(bike, label) {
    const evt = new CustomEvent(EVENT_NAME, {
      detail: { bike, label },
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(evt);
  }

  /* ---------------------------------------------------------------- */
  /* Year/Make/Model cascade derivation                                */
  /* ---------------------------------------------------------------- */

  /**
   * For each bike, expand its year range into a list of years.
   * Caller provides the picker year — we filter bikes whose [year_min, year_max] contains that year.
   */
  function bikesForYear(bikes, year) {
    return bikes.filter((b) => year >= b.year_min && year <= b.year_max);
  }

  function uniqueSorted(arr) {
    return Array.from(new Set(arr)).sort();
  }

  function uniqueYearsDescending(bikes) {
    const years = new Set();
    for (const b of bikes) {
      for (let y = b.year_min; y <= b.year_max; y++) years.add(y);
    }
    return Array.from(years).sort((a, b) => b - a);
  }

  /* ---------------------------------------------------------------- */
  /* The custom element                                                */
  /* ---------------------------------------------------------------- */

  class MotorcycleSelector extends HTMLElement {
    constructor() {
      super();
      this._bikes = null;
      this._selectedBike = null;
      this._year = null;
      this._draft = { year: null, make: null, model: null, engine: null };
    }

    connectedCallback() {
      this._jsonUrl = this.getAttribute('data-bikes-url');
      if (!this._jsonUrl) {
        console.warn('<motorcycle-selector>: missing data-bikes-url attribute');
        return;
      }

      this._renderClosed();

      // Resolve initial selection: URL param overrides localStorage
      const fromUrl = readUrlParam();
      const fromStorage = readStorage();
      const initialId = fromUrl || fromStorage;

      const restored = initialId
        ? loadBikes(this._jsonUrl).then((bikes) => {
            this._bikes = bikes;
            const year = readStoredYear();
            const id = migrateId(initialId, year);
            const match = bikes.find((b) => b.id === id);
            if (match) {
              this._setSelection(match, /*announce=*/ true, /*touchUrl=*/ id !== initialId && !!fromUrl, false, year);
            }
          })
        : Promise.resolve();

      this._onHash = () => {
        if (window.location.hash !== OPEN_HASH) return;
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this._openDialog();
        // Drop the hash so a reload doesn't reopen the selector.
        try {
          const url = new URL(window.location.href);
          url.hash = '';
          window.history.replaceState({}, '', url.toString());
        } catch (_e) {
          /* ignore */
        }
      };
      window.addEventListener('hashchange', this._onHash);
      // Wait for the saved bike to render first, or it would close the dialog.
      restored.catch(() => {}).then(this._onHash);
    }

    disconnectedCallback() {
      if (this._onHash) window.removeEventListener('hashchange', this._onHash);
    }

    /* ----------------- public API ---------------------- */

    get selectedBike() {
      return this._selectedBike;
    }

    clearSelection() {
      this._setSelection(null, true, true);
    }

    /* ----------------- rendering ----------------------- */

    _renderClosed(restoreFocus) {
      this.innerHTML = '';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'motorcycle-selector__trigger';
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.setAttribute('aria-expanded', 'false');

      if (this._selectedBike) {
        btn.classList.add('motorcycle-selector__trigger--selected');
        btn.innerHTML =
          '<span class="motorcycle-selector__label">Your bike</span>' +
          `<span class="motorcycle-selector__bike">${this._escape(bikeLabel(this._selectedBike, this._year))}</span>` +
          '<span class="motorcycle-selector__change">Change</span>';
      } else {
        btn.innerHTML =
          '<svg class="motorcycle-selector__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          '<path d="M5 18a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm14 0a3 3 0 1 1 0-6 3 3 0 0 1 0 6ZM7 12h6.5l2-3.5h2.4l-2 3.5H19a5 5 0 1 1-2.91 9.06L13 16H9.91A5 5 0 1 1 7 12Z" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linejoin="round"/>' +
          '</svg>' +
          '<span class="motorcycle-selector__label">What do you ride?</span>' +
          '<span class="motorcycle-selector__chevron" aria-hidden="true">›</span>';
      }
      btn.addEventListener('click', () => this._openDialog());
      this.appendChild(btn);
      if (restoreFocus) btn.focus();
    }

    _openDialog() {
      // Pre-fetch bikes if we haven't yet
      loadBikes(this._jsonUrl)
        .then((bikes) => {
          this._bikes = bikes;
          this._renderDialog();
        })
        .catch(() => this._renderLoadError());
    }

    _renderLoadError() {
      this.innerHTML = '';
      const box = document.createElement('div');
      box.className = 'motorcycle-selector__dialog motorcycle-selector__dialog--error';
      box.setAttribute('role', 'alert');
      box.innerHTML =
        '<p class="motorcycle-selector__error">Couldn\u2019t load the bike list. Check your connection and try again.</p>' +
        '<div class="motorcycle-selector__dialog-footer">' +
        '<button type="button" class="motorcycle-selector__clear">Cancel</button>' +
        '<button type="button" class="motorcycle-selector__confirm">Try again</button>' +
        '</div>';
      box.querySelector('.motorcycle-selector__clear').addEventListener('click', () => this._renderClosed(true));
      const retry = box.querySelector('.motorcycle-selector__confirm');
      retry.addEventListener('click', () => this._openDialog());
      this.appendChild(box);
      retry.focus();
    }

    _renderDialog() {
      // Reset draft with current selection if any
      if (this._selectedBike) {
        this._draft = {
          year: this._year || this._selectedBike.year_max,
          make: this._selectedBike.make,
          model: this._selectedBike.model,
          engine: this._selectedBike.engine,
        };
      } else {
        this._draft = { year: null, make: null, model: null, engine: null };
      }

      // Build the dialog
      this.innerHTML = '';
      const dialog = document.createElement('div');
      dialog.className = 'motorcycle-selector__dialog';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-label', 'What do you ride?');

      dialog.innerHTML = `
        <div class="motorcycle-selector__dialog-header">
          <h2 class="motorcycle-selector__dialog-title">What do you ride?</h2>
          <button type="button" class="motorcycle-selector__close" aria-label="Close">×</button>
        </div>
        <div class="motorcycle-selector__dialog-body">
          <label class="motorcycle-selector__field">
            <span>Year</span>
            <select data-axis="year"><option value="">Select year</option></select>
          </label>
          <label class="motorcycle-selector__field">
            <span>Make</span>
            <select data-axis="make" disabled><option value="">Select make</option></select>
          </label>
          <label class="motorcycle-selector__field">
            <span>Model</span>
            <select data-axis="model" disabled><option value="">Select model</option></select>
          </label>
          <label class="motorcycle-selector__field">
            <span>Engine / Trim</span>
            <select data-axis="engine" disabled><option value="">Select engine</option></select>
          </label>
        </div>
        <div class="motorcycle-selector__dialog-footer">
          ${this._selectedBike ? '<button type="button" class="motorcycle-selector__clear">Clear selection</button>' : '<span></span>'}
          <button type="button" class="motorcycle-selector__confirm" disabled>Show parts for my bike</button>
        </div>
      `;

      this.appendChild(dialog);

      const yearSel = dialog.querySelector('select[data-axis="year"]');
      const makeSel = dialog.querySelector('select[data-axis="make"]');
      const modelSel = dialog.querySelector('select[data-axis="model"]');
      const engineSel = dialog.querySelector('select[data-axis="engine"]');
      const confirm = dialog.querySelector('.motorcycle-selector__confirm');

      // Populate years from data (descending)
      uniqueYearsDescending(this._bikes).forEach((y) => {
        yearSel.appendChild(this._option(String(y), String(y), y === this._draft.year));
      });

      const onChange = (axis, value) => {
        this._draft[axis] = value || null;
        // Cascade reset
        if (axis === 'year') {
          this._draft.make = this._draft.model = this._draft.engine = null;
        } else if (axis === 'make') {
          this._draft.model = this._draft.engine = null;
        } else if (axis === 'model') {
          this._draft.engine = null;
        }
        this._refreshCascades(yearSel, makeSel, modelSel, engineSel);
        confirm.disabled = !this._draftIsComplete();
      };

      yearSel.addEventListener('change', (e) => onChange('year', Number(e.target.value)));
      makeSel.addEventListener('change', (e) => onChange('make', e.target.value));
      modelSel.addEventListener('change', (e) => onChange('model', e.target.value));
      engineSel.addEventListener('change', (e) => onChange('engine', e.target.value));

      dialog
        .querySelector('.motorcycle-selector__close')
        .addEventListener('click', () => this._renderClosed(true));
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this._renderClosed(true);
        }
      });
      const clearBtn = dialog.querySelector('.motorcycle-selector__clear');
      if (clearBtn)
        clearBtn.addEventListener('click', () => {
          this._setSelection(null, true, true, true);
          this._goToParts(null);
        });

      confirm.addEventListener('click', () => {
        const bike = this._matchedBike();
        if (!bike) return;
        this._setSelection(bike, true, true, true, this._draft.year);
        this._goToParts(bike.id);
      });

      // If we have an existing selection, refresh the cascades to populate make/model/engine
      if (this._selectedBike) {
        this._refreshCascades(yearSel, makeSel, modelSel, engineSel);
        confirm.disabled = !this._draftIsComplete();
      }

      yearSel.focus();
    }

    _refreshCascades(yearSel, makeSel, modelSel, engineSel) {
      const { year } = this._draft;
      const candidates = year ? bikesForYear(this._bikes, year) : [];

      // A field with exactly one choice is filled in for the rider.
      const autoPick = (axis, values) => {
        if (!this._draft[axis] && values.length === 1) this._draft[axis] = values[0];
      };

      // Makes
      const makes = uniqueSorted(candidates.map((b) => b.make));
      if (year) autoPick('make', makes);
      const make = this._draft.make;
      this._fillSelect(makeSel, ['Select make', ...makes], make, !year);

      // Models (filtered by make)
      const modelsCandidates = make ? candidates.filter((b) => b.make === make) : [];
      const models = uniqueSorted(modelsCandidates.map((b) => b.model));
      if (make) autoPick('model', models);
      const model = this._draft.model;
      this._fillSelect(modelSel, ['Select model', ...models], model, !make);

      // Engines (filtered by make + model)
      const engineCandidates = model ? modelsCandidates.filter((b) => b.model === model) : [];
      const engines = uniqueSorted(engineCandidates.map((b) => b.engine || '—'));
      if (model) autoPick('engine', engines);
      this._fillSelect(engineSel, ['Select engine', ...engines], this._draft.engine || '—', !model);
    }

    _fillSelect(sel, options, currentValue, disabled) {
      sel.innerHTML = '';
      options.forEach((label, i) => {
        const isPlaceholder = i === 0;
        const opt = this._option(
          isPlaceholder ? '' : label,
          label,
          !isPlaceholder && label === currentValue
        );
        sel.appendChild(opt);
      });
      sel.disabled = !!disabled;
    }

    _option(value, label, selected) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      if (selected) opt.selected = true;
      return opt;
    }

    _draftIsComplete() {
      return this._draft.year && this._draft.make && this._draft.model && this._matchedBike();
    }

    _matchedBike() {
      const { year, make, model, engine } = this._draft;
      if (!year || !make || !model) return null;
      return this._bikes.find(
        (b) =>
          b.make === make &&
          b.model === model &&
          year >= b.year_min &&
          year <= b.year_max &&
          (engine === '—' || engine === null || b.engine === engine)
      );
    }

    _setSelection(bike, announce, touchUrl, restoreFocus, year) {
      this._selectedBike = bike || null;
      // Keep the rider's own year when it falls in the entry's range.
      const y = year || this._year;
      this._year = bike && y >= bike.year_min && y <= bike.year_max ? y : null;
      const label = bike ? bikeLabel(bike, this._year) : '';
      writeStorage(bike ? bike.id : null, this._year || '', label);
      if (touchUrl) writeUrlParam(bike ? bike.id : null);
      this._renderClosed(restoreFocus);
      if (announce) dispatchSelection(this._selectedBike, label);
    }

    /**
     * Show the parts that fit: swap the bike filter in place on a filterable
     * listing, or go to the category hub from anywhere else.
     */
    _goToParts(bikeId) {
      // The category hub re-labels its own links on the selection event.
      if (document.querySelector('[data-category-hub]')) return;
      const onListing = !!document.getElementById('ProductGridContainer');
      if (onListing) {
        // Swap the bike filter in place where Shopify can filter this list;
        // elsewhere the cards and category chips re-tag themselves.
        if (!document.querySelector('fitment-bar[data-filter-available]')) return;
        const dest = new URL(window.location.href);
        dest.searchParams.delete(FILTER_PARAM);
        dest.searchParams.delete('page');
        dest.hash = '';
        if (bikeId) dest.searchParams.set(FILTER_PARAM, bikeId);
        if (dest.toString() !== window.location.href) window.location.assign(dest.toString());
        return;
      }
      if (bikeId) {
        window.location.assign(new URL(this.getAttribute('data-results-url') || DEFAULT_RESULTS_URL, window.location.origin).toString());
      }
    }

    _escape(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('motorcycle-selector')) {
    customElements.define('motorcycle-selector', MotorcycleSelector);
  }
})();
